import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { motion, AnimatePresence } from 'framer-motion';
import {
  Send,
  ArrowLeft,
  MoreVertical,
  Mic,
  Image,
  Paperclip,
  Check,
  Lock,
  Search,
  Brain,
  Sun,
  Moon,
  Sparkles,
  Shield,
  Zap,
  ChevronRight,
  MessageCircle,
  Bot,
  Star,
  CheckCheck,
  X,
  ChevronDown,
  Users,
  User,
  VolumeX,
  Activity,
  Award,
  Heart,
  Plus,
  FileText,
  MapPin,
  Trash2,
  Play,
  Pause
} from 'lucide-react';
import { useUI } from '../contexts/UIContext';
import BottomNavigation from '../components/BottomNavigation';


/**
 * ✨ Premium Mesajlaşma Ekranı - IDAPPS
 * Gen Z / Alpha kuşağına hitap eden modern, premium tasarım
 * Digital Clone AI her zaman en üstte pinli
 */

// Digital Clone AI Conversation Data
const DIGITAL_CLONE_CHAT = {
  id: 'digital-clone',
  name: 'Digital Clone',
  avatar: null,
  isAI: true,
  lastMessage: 'Bugün sana nasıl yardımcı olabilirim?',
  time: 'Şimdi',
  unread: 0,
  online: true,
  verified: true,
  messages: [
    { id: 1, text: 'Merhaba! Ben senin Digital Clone\'un. 🧠', sender: 'them', time: '00:00', isAI: true },
    { id: 2, text: 'Seni daha iyi tanıdıkça, mesajlarına daha akıllı öneriler sunacağım.', sender: 'them', time: '00:00', isAI: true },
    { id: 3, text: 'Bugün sana nasıl yardımcı olabilirim?', sender: 'them', time: 'Şimdi', isAI: true },
  ]
};

// Chat Card Component - Premium Design
const ChatCard = ({ chat, onClick, isDarkMode, isDigitalClone = false }) => {
  const getInitials = (name) => name.charAt(0).toUpperCase();

  // Gradient colors for different chats
  const avatarGradients = [
    'from-blue-500 to-cyan-400',
    'from-purple-500 to-pink-400',
    'from-amber-500 to-orange-400',
    'from-emerald-500 to-teal-400',
    'from-rose-500 to-red-400',
  ];

  const gradientIndex = chat.id.toString().charCodeAt(0) % avatarGradients.length;

  return (
    <motion.div
      whileHover={{ scale: 1.01, x: 4 }}
      whileTap={{ scale: 0.98 }}
      onClick={onClick}
      className={`relative flex items-center p-4 cursor-pointer transition-all rounded-2xl mb-2 mx-2 ${isDigitalClone
        ? '' // Digital Clone has its own styling
        : isDarkMode
          ? 'bg-slate-800/50 hover:bg-slate-800'
          : 'bg-white hover:bg-slate-50 shadow-sm'
        }`}
      style={isDigitalClone ? {
        background: 'linear-gradient(135deg, #8B5CF6 0%, #EC4899 50%, #F97316 100%)',
        boxShadow: '0 8px 32px rgba(139, 92, 246, 0.3)'
      } : {}}
    >
      {/* Avatar */}
      <div className="relative mr-4">
        <div
          className={`w-14 h-14 rounded-2xl flex items-center justify-center font-bold text-lg ${isDigitalClone
            ? 'bg-white/20 backdrop-blur-sm'
            : `bg-gradient-to-br ${avatarGradients[gradientIndex]}`
            }`}
        >
          {isDigitalClone ? (
            <Brain size={28} className="text-white" />
          ) : (
            <span className="text-white">{getInitials(chat.name)}</span>
          )}
        </div>

        {/* Online indicator with pulse */}
        {chat.online && (
          <div className="absolute -bottom-0.5 -right-0.5">
            <div className="w-4 h-4 bg-emerald-500 rounded-full border-2 border-white dark:border-slate-900 flex items-center justify-center">
              <motion.div
                animate={{ scale: [1, 1.3, 1], opacity: [1, 0.5, 1] }}
                transition={{ duration: 2, repeat: Infinity }}
                className="w-2 h-2 bg-emerald-400 rounded-full"
              />
            </div>
          </div>
        )}

        {/* Verified badge */}
        {chat.verified && !isDigitalClone && (
          <div className="absolute -top-1 -right-1 w-5 h-5 bg-blue-500 rounded-full flex items-center justify-center ring-2 ring-white dark:ring-slate-900">
            <Check size={10} className="text-white" strokeWidth={3} />
          </div>
        )}

        {/* AI Sparkle for Digital Clone */}
        {isDigitalClone && (
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 8, repeat: Infinity, ease: "linear" }}
            className="absolute -top-1 -right-1"
          >
            <Sparkles size={18} className="text-white" />
          </motion.div>
        )}
      </div>

      {/* Chat Info */}
      <div className="flex-1 min-w-0">
        <div className="flex justify-between items-center mb-1">
          <h3 className={`font-bold truncate ${isDigitalClone ? 'text-white' : isDarkMode ? 'text-white' : 'text-slate-900'
            }`}>
            {chat.name}
            {isDigitalClone && (
              <span className="ml-2 text-xs font-medium text-white/70 bg-white/20 px-2 py-0.5 rounded-full">
                AI
              </span>
            )}
          </h3>
          <span className={`text-xs font-medium ${isDigitalClone ? 'text-white/60' : isDarkMode ? 'text-slate-500' : 'text-slate-400'
            }`}>
            {chat.time}
          </span>
        </div>

        <div className="flex justify-between items-center">
          <p className={`text-sm truncate ${isDigitalClone ? 'text-white/80' : isDarkMode ? 'text-slate-400' : 'text-slate-500'
            }`}>
            {isDigitalClone && <Bot size={12} className="inline mr-1" />}
            {chat.lastMessage}
          </p>

          {chat.unread > 0 && (
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              className="ml-2 bg-gradient-to-r from-indigo-500 to-purple-500 text-white text-xs font-bold rounded-full min-w-[20px] h-5 flex items-center justify-center px-1.5"
            >
              {chat.unread}
            </motion.div>
          )}
        </div>
      </div>

      {/* Arrow indicator */}
      <ChevronRight size={18} className={`ml-2 ${isDigitalClone ? 'text-white/50' : isDarkMode ? 'text-slate-600' : 'text-slate-300'
        }`} />

      {/* Decorative glow for Digital Clone */}
      {isDigitalClone && (
        <>
          <div className="absolute -bottom-10 -right-10 w-32 h-32 bg-white/10 rounded-full blur-2xl pointer-events-none" />
          <div className="absolute top-0 right-20 w-16 h-16 bg-pink-400/20 rounded-full blur-xl pointer-events-none" />
        </>
      )}
    </motion.div>
  );
};

// Tab Item Component
const TabItem = ({ active, label, onClick, isDarkMode }) => (
  <motion.button
    whileTap={{ scale: 0.95 }}
    onClick={onClick}
    className={`relative px-6 py-2 font-semibold text-sm transition-colors ${active
      ? isDarkMode ? 'text-white' : 'text-slate-900'
      : isDarkMode ? 'text-slate-500' : 'text-slate-400'
      }`}
  >
    {label}
    {active && (
      <motion.div
        layoutId="activeTab"
        className="absolute bottom-0 left-2 right-2 h-0.5 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full"
      />
    )}
  </motion.button>
);

// Audio Message Player Component
const AudioMessagePlayer = ({ audioUrl, duration, isMe, isDarkMode }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const audioRef = useRef(null);

  const togglePlay = () => {
    if (!audioRef.current) {
      audioRef.current = new Audio(audioUrl);
      audioRef.current.onended = () => setIsPlaying(false);
    }

    if (isPlaying) {
      audioRef.current.pause();
    } else {
      audioRef.current.play();
    }
    setIsPlaying(!isPlaying);
  };

  return (
    <div className="flex items-center gap-3 min-w-[160px]">
      <button
        onClick={togglePlay}
        className={`p-2 rounded-full ${isMe ? 'bg-white/20 text-white' : isDarkMode ? 'bg-slate-700 text-white' : 'bg-slate-100 text-slate-800'}`}
      >
        {isPlaying ? <Pause size={16} fill="currentColor" /> : <Play size={16} fill="currentColor" />}
      </button>
      <div className="flex-1 flex flex-col justify-center gap-1">
        <div className="h-4 flex items-center gap-0.5 opacity-80">
          {/* Fake Waveform */}
          {[...Array(12)].map((_, i) => (
            <div
              key={i}
              className={`w-1 rounded-full ${isMe ? 'bg-white' : isDarkMode ? 'bg-slate-400' : 'bg-slate-500'}`}
              style={{
                height: `${Math.max(20, Math.random() * 100)}%`,
                opacity: isPlaying ? 1 : 0.6,
                animation: isPlaying ? `pulse 0.5s infinite ${i * 0.1}s` : 'none'
              }}
            />
          ))}
        </div>
        <span className={`text-[10px] font-mono ${isMe ? 'text-white/80' : isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
          {Math.floor(duration / 60)}:{duration % 60 < 10 ? '0' : ''}{duration % 60}
        </span>
      </div>
    </div>
  );
};

// AI Suggestion Chip
const AISuggestionChip = ({ text, onClick, isDarkMode }) => (
  <motion.button
    whileHover={{ scale: 1.02 }}
    whileTap={{ scale: 0.98 }}
    onClick={onClick}
    className={`px-4 py-2.5 rounded-2xl text-sm font-medium whitespace-nowrap flex-shrink-0 transition-all ${isDarkMode
      ? 'bg-gradient-to-r from-slate-800 to-slate-700 text-white border border-slate-600 hover:border-purple-500/50'
      : 'bg-white text-gray-900 border border-gray-200 shadow-sm hover:border-purple-300'
      }`}
  >
    <Zap size={12} className={`inline mr-1.5 ${isDarkMode ? 'text-purple-400' : 'text-purple-500'}`} />
    {text}
  </motion.button>
);

// AI Message Bubble with special styling
const MessageBubble = ({ message, isDarkMode }) => {
  const isMe = message.sender === 'me';
  const isAI = message.isAI;

  return (
    <motion.div
      initial={{ opacity: 0, y: 10, scale: 0.95 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ duration: 0.2 }}
      className={`flex ${isMe ? 'justify-end' : 'justify-start'} mb-3`}
    >
      <div
        className={`relative max-w-[80%] px-4 py-3 rounded-2xl ${isMe
          ? 'bg-gradient-to-br from-blue-500 to-indigo-600 text-white rounded-br-md'
          : isAI
            ? isDarkMode ? 'bg-slate-800 border-l-4 border-purple-500 rounded-bl-md text-white' : 'bg-white border-l-4 border-purple-500 shadow-sm rounded-bl-md text-slate-800'
            : isDarkMode
              ? 'bg-slate-800 rounded-bl-md text-white'
              : 'bg-white shadow-sm rounded-bl-md text-slate-800'
          } ${isAI ? 'p-5' : ''}`}
      >
        {isAI && (
          <div className={`flex items-center gap-2 mb-2 text-xs font-semibold ${isDarkMode ? 'text-purple-400' : 'text-purple-600'}`}>
            <Sparkles size={10} />
            <span>Digital Clone</span>
          </div>
        )}

        {message.type === 'audio' ? (
          <AudioMessagePlayer
            audioUrl={message.audioUrl}
            duration={message.duration}
            isMe={isMe}
            isDarkMode={isDarkMode}
          />
        ) : (
          <p className="text-sm leading-relaxed">{message.text}</p>
        )}

        <div className={`text-[10px] mt-1.5 flex items-center justify-end gap-1 ${isMe ? 'text-white/60' : isAI ? 'text-purple-300/60' : isDarkMode ? 'text-slate-500' : 'text-slate-400'
          }`}>
          {message.time}
          {isMe && <CheckCheck size={12} className="text-cyan-300" />}
        </div>
      </div>
    </motion.div>
  );
};

const MessagingScreen = () => {
  const { navigateToScreen, isDarkMode, toggleTheme, fontSizeScale } = useUI();
  const { t } = useTranslation();

  // Refs for specific attachment inputs
  const fileInputRef = useRef(null);
  const imageInputRef = useRef(null);

  // UI durumları
  const [activeTab, setActiveTab] = useState('chats');
  const [currentChat, setCurrentChat] = useState(null);
  const [message, setMessage] = useState('');
  const [isAIEnabled, setIsAIEnabled] = useState(true);
  const [aiSuggestions, setAiSuggestions] = useState([]);
  const [privacyLevel, setPrivacyLevel] = useState('end-to-end');
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearchFocused, setIsSearchFocused] = useState(false);

  // New State for Consolidated Input
  const [showAttachMenu, setShowAttachMenu] = useState(false);

  // Voice Recording State
  const [voiceState, setVoiceState] = useState({
    isRecording: false,
    recordingDuration: 0,
    audioBlob: null,
    audioUrl: null,
    isPlaying: false,
    previewDuration: 0,
    previewCurrentTime: 0
  });

  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const recordingTimerRef = useRef(null);
  const previewAudioRef = useRef(null);
  const recordingTimeoutRef = useRef(null);
  const suggestionTimerRef = useRef(null);

  // AI Öneri Yenileme Süresi (saniye)
  const [suggestionRefreshDelay, setSuggestionRefreshDelay] = useState(15);

  // Ses kaydı başlatma
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/mp3' });
        const audioUrl = URL.createObjectURL(audioBlob);
        setVoiceState(prev => ({
          ...prev,
          isRecording: false,
          audioBlob,
          audioUrl
        }));

        // Stop all tracks
        stream.getTracks().forEach(track => track.stop());
      };

      mediaRecorder.start();
      setVoiceState(prev => ({ ...prev, isRecording: true, recordingDuration: 0 }));

      // Timer
      recordingTimerRef.current = setInterval(() => {
        setVoiceState(prev => ({ ...prev, recordingDuration: prev.recordingDuration + 1 }));
      }, 1000);

    } catch (error) {
      console.error("Mikrofon hatası:", error);
      alert("Mikrofona erişilemedi. Lütfen izin verin.");
    }
  };

  // Ses kaydı durdurma (Önizleme moduna geçer)
  const stopRecording = () => {
    if (mediaRecorderRef.current && voiceState.isRecording) {
      mediaRecorderRef.current.stop();
      clearInterval(recordingTimerRef.current);
    }
  };

  // Kaydı iptal et
  const cancelRecording = () => {
    setVoiceState({
      isRecording: false,
      recordingDuration: 0,
      audioBlob: null,
      audioUrl: null,
      isPlaying: false,
      previewDuration: 0,
      previewCurrentTime: 0
    });
    if (voiceState.audioUrl) {
      URL.revokeObjectURL(voiceState.audioUrl);
    }
  };

  // Önizlemeyi Oynat/Durdur
  const togglePreviewPlay = () => {
    if (!previewAudioRef.current) {
      previewAudioRef.current = new Audio(voiceState.audioUrl);
      previewAudioRef.current.onended = () => {
        setVoiceState(prev => ({ ...prev, isPlaying: false }));
      };
    }

    if (voiceState.isPlaying) {
      previewAudioRef.current.pause();
    } else {
      previewAudioRef.current.play();
    }
    setVoiceState(prev => ({ ...prev, isPlaying: !prev.isPlaying }));
  };

  // Süre formatlama (MM:SS)
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs < 10 ? '0' : ''}${secs}`;
  };
  const [showProfileModal, setShowProfileModal] = useState(null);
  const messagesEndRef = useRef(null);

  // Eklenti İşlemleri (Galeri, Belge, Konum)
  const handleAttachment = (type) => {
    setShowAttachMenu(false);

    switch (type) {
      case 'gallery':
        if (imageInputRef.current) imageInputRef.current.click();
        break;
      case 'document':
        if (fileInputRef.current) fileInputRef.current.click();
        break;
      case 'location':
        // Konum simülasyonu
        const updatedChat = { ...currentChat };
        updatedChat.messages.push({
          id: updatedChat.messages.length + 1,
          text: '📍 Konum: İstanbul, Türkiye (41.0082° N, 28.9784° E)',
          sender: 'me',
          time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
        });
        setCurrentChat(updatedChat);
        break;
      default:
        break;
    }
  };

  // Dosya seçildiğinde (Simüle edilmiş - ismi mesaja yazar)
  const handleFileChange = (e, type) => {
    const file = e.target.files[0];
    if (file) {
      const updatedChat = { ...currentChat };
      updatedChat.messages.push({
        id: updatedChat.messages.length + 1,
        text: type === 'image' ? `📷 Resim gönderildi: ${file.name}` : `📄 Belge gönderildi: ${file.name}`,
        sender: 'me',
        time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
      });
      setCurrentChat(updatedChat);
    }
  };

  // Tema sistemi - TodayScreen'den adapte edildi
  const theme = {
    background: isDarkMode ? 'bg-slate-950' : 'bg-gray-50',
    text: isDarkMode ? 'text-white' : 'text-slate-900',
    subText: isDarkMode ? 'text-slate-400' : 'text-slate-500',
    card: isDarkMode ? 'bg-slate-900' : 'bg-white',
    headerBg: isDarkMode ? 'bg-slate-900/80' : 'bg-white/90',
    border: isDarkMode ? 'border-slate-800' : 'border-slate-100',
    inputBg: isDarkMode ? 'bg-slate-800' : 'bg-slate-100',
  };

  // Sekme öğeleri
  const tabItems = [
    { id: 'chats', label: t('screens.chatsTab', 'Sohbetler') },
    { id: 'groups', label: t('screens.groupsTab', 'Gruplar') },
    { id: 'secure', label: t('security.secure', 'Güvenli') }
  ];

  // Örnek sohbet verileri
  const conversations = [
    {
      id: 1,
      name: 'Ömer Köse',
      avatar: null,
      lastMessage: 'Tasarımları hallettin mi?',
      time: '17:18',
      unread: 0,
      online: true,
      verified: true,
      messages: [
        { id: 1, text: t('messaging.greeting', 'Selam, nasılsın?'), sender: 'them', time: '16:42' },
        { id: 2, text: t('messaging.imGood', 'İyiyim, sen?'), sender: 'me', time: '16:45' },
        { id: 3, text: t('messaging.imGoodToo', 'Ben de iyiyim, teşekkürler.'), sender: 'them', time: '16:47' },
        { id: 4, text: 'IDapps için tasarımları hallettin mi?', sender: 'them', time: '17:18' },
      ]
    },
    {
      id: 2,
      name: 'Melih Karataş',
      avatar: null,
      lastMessage: t('messaging.sentToYou', 'Gönderdim sana.'),
      time: '17:18',
      unread: 0,
      online: false,
      verified: true,
      messages: [
        { id: 1, text: t('messaging.canYouSendFiles', 'Dosyaları gönderebilir misin?'), sender: 'them', time: '16:00' },
        { id: 2, text: t('messaging.sentToYou', 'Gönderdim sana.'), sender: 'me', time: '17:18' },
      ]
    },
    {
      id: 3,
      name: 'Said Emre Gün',
      avatar: null,
      lastMessage: t('messaging.agreed', 'Anlaştık.'),
      time: '17:18',
      unread: 0,
      online: true,
      verified: false,
      messages: [
        { id: 1, text: t('messaging.projectCostQuestion', 'Merhaba, projenin maliyeti hakkında konuşabilir miyiz?'), sender: 'them', time: '15:30' },
        { id: 2, text: t('messaging.whatDoYouThink', 'Tabii, ne düşünüyorsun?'), sender: 'me', time: '15:35' },
        { id: 3, text: t('messaging.priceProposal', 'Bence 5000₺ uygun bir fiyat.'), sender: 'them', time: '16:00' },
        { id: 4, text: t('messaging.agreed', 'Anlaştık.'), sender: 'me', time: '17:18' },
      ]
    },
    {
      id: 4,
      name: 'Furkan Rıza Demirel',
      avatar: null,
      lastMessage: t('messaging.seeYou', 'Tamamdır, görüşürüz.'),
      time: '17:18',
      unread: 0,
      online: false,
      verified: true,
      messages: [
        { id: 1, text: t('messaging.meetingInvite', 'Yarın toplantı saat 15:00\'te, katılabilir misin?'), sender: 'them', time: '14:20' },
        { id: 2, text: t('messaging.seeYou', 'Tamamdır, görüşürüz.'), sender: 'me', time: '17:18' },
      ]
    },
    {
      id: 5,
      name: 'Asya Aktunç',
      avatar: null,
      lastMessage: t('messaging.callMeWhenFree', 'Müsait olduğunda beni arar mısın?'),
      time: '17:18',
      unread: 1,
      online: true,
      verified: true,
      messages: [
        { id: 1, text: t('messaging.callMeWhenFree', 'Müsait olduğunda beni arar mısın?'), sender: 'them', time: '17:18' },
      ]
    },
  ];

  // AI önerilerini simüle eden fonksiyon
  const generateAiSuggestions = (chat) => {
    if (!chat) return [];

    const lastMessage = chat.messages[chat.messages.length - 1];

    // Digital Clone için özel öneriler
    if (chat.isAI) {
      return [
        'Bugünkü görevlerimi göster',
        'Bana disiplin ipuçları ver',
        'Hedeflerimi hatırlat'
      ];
    }

    // Diğer tüm sohbetler için bağlamsal veya genel öneriler
    if (lastMessage && lastMessage.sender === 'them') {
      if (lastMessage.text.includes('tasarımları')) {
        return [
          t('messaging.aiSuggestion1', 'Evet, tamamladım. Birazdan göndereceğim.'),
          t('messaging.aiSuggestion2', 'Üzerinde çalışıyorum, yarına hazır olacak.'),
          t('messaging.aiSuggestion3', 'Henüz bitirmedim, bu hafta içinde tamamlayacağım.')
        ];
      } else if (lastMessage.text.includes('arar mısın')) {
        return [
          t('messaging.aiSuggestion4', 'Tabii, 10 dakika içinde arıyorum.'),
          t('messaging.aiSuggestion5', 'Şu an toplantıdayım, 1 saat sonra arayabilir miyim?'),
          t('messaging.aiSuggestion6', 'Bugün müsait değilim, yarın arasam olur mu?')
        ];
      }
    }

    // Varsayılan genel öneriler (Her zaman gösterilir)
    return [
      'Tamam, harika! 👍',
      'Teşekkürler, anlaştık.',
      'Daha sonra konuşalım mı?',
      'Konumu gönderir misin?',
      'Müsait olduğunda ara.'
    ];
  };

  // Mesajlar bölümünün en altına otomatik kaydırma
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [currentChat]);

  // AI Önerileri Otomatik Yenileme (klavye hareketsizliği)
  useEffect(() => {
    if (!currentChat || !isAIEnabled) return;

    // Timer'ı temizle ve yeniden başlat
    if (suggestionTimerRef.current) {
      clearTimeout(suggestionTimerRef.current);
    }

    suggestionTimerRef.current = setTimeout(() => {
      const newSuggestions = generateAiSuggestions(currentChat);
      setAiSuggestions(newSuggestions);
    }, suggestionRefreshDelay * 1000);

    return () => {
      if (suggestionTimerRef.current) {
        clearTimeout(suggestionTimerRef.current);
      }
    };
  }, [message, currentChat, suggestionRefreshDelay, isAIEnabled]);

  // Sohbet seçildiğinde
  const handleSelectChat = (chat) => {
    setCurrentChat(chat);
    setShowAttachMenu(false); // Reset attachment menu
    const suggestions = generateAiSuggestions(chat);
    setAiSuggestions(suggestions);
  };

  // Mesaj gönderme işlemi
  const handleSendMessage = () => {
    if (message.trim() === '' && !voiceState.audioUrl) return;

    const updatedChat = { ...currentChat };
    const newMessage = {
      id: updatedChat.messages.length + 1,
      sender: 'me',
      time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    };

    if (voiceState.audioUrl) {
      newMessage.type = 'audio';
      newMessage.audioUrl = voiceState.audioUrl;
      newMessage.duration = voiceState.recordingDuration;

      // Reset voice state
      setVoiceState({
        isRecording: false,
        recordingDuration: 0,
        audioBlob: null,
        audioUrl: null,
        isPlaying: false,
        previewDuration: 0,
        previewCurrentTime: 0
      });
    } else {
      newMessage.text = message;
      setMessage('');
    }

    updatedChat.messages.push(newMessage);
    setCurrentChat(updatedChat);

    // Mesaj gönderildikten sonra yeni öneriler oluştur
    setTimeout(() => {
      const newSuggestions = generateAiSuggestions(updatedChat);
      setAiSuggestions(newSuggestions);
    }, 500);

    // Digital Clone için AI yanıtı simülasyonu
    if (currentChat.isAI) {
      setTimeout(() => {
        const aiResponses = [
          'Anladım! Sana bu konuda yardımcı olabilirim. 🎯',
          'Harika bir soru! İşte düşüncelerim...',
          'Bu konuyu daha detaylı inceleyeyim.',
          'Sesli mesajını aldım, dinleyip dönüyorum 🎧',
        ];
        const randomResponse = aiResponses[Math.floor(Math.random() * aiResponses.length)];

        setCurrentChat(prev => ({
          ...prev,
          messages: [...prev.messages, {
            id: prev.messages.length + 1,
            text: randomResponse,
            sender: 'them',
            time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
            isAI: true
          }]
        }));

        setAiSuggestions(['Daha fazla bilgi ver', 'Başka bir şey sor', 'Teşekkürler']);
      }, 1500);
    }
  };

  // AI önerisini kullanma
  const applySuggestion = (suggestion) => {
    setMessage(suggestion);
  };

  // Filtrelenmiş sohbetleri döndüren fonksiyon
  const getFilteredConversations = () => {
    if (!searchQuery) return conversations;
    return conversations.filter(conv =>
      conv.name.toLowerCase().includes(searchQuery.toLowerCase())
    );
  };

  // Gizlilik düzeyi bilgisi
  const getPrivacyInfo = () => {
    switch (privacyLevel) {
      case 'enhanced':
        return t('security.enhancedSecurityMode', 'Gelişmiş güvenlik modu aktif');
      case 'max':
        return t('security.maxSecurityMode', 'Maksimum güvenlik modu aktif');
      case 'end-to-end':
      default:
        return t('security.endToEndEncryption', 'Uçtan uca şifrelenmiş');
    }
  };

  // Gizlilik düzeyi rengi
  const getPrivacyColor = () => {
    switch (privacyLevel) {
      case 'enhanced': return 'text-amber-400';
      case 'max': return 'text-red-400';
      default: return 'text-emerald-400';
    }
  };

  return (
    <div
      className={`flex flex-col h-[100dvh] ${theme.background} relative overflow-hidden`}
      style={{
        fontSize: `calc(1rem * var(--font-size-scale, ${fontSizeScale || 1}))`
      }}
    >
      {/* Subtle gradient background */}
      {!isDarkMode && (
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background: 'linear-gradient(180deg, #F8FAFC 0%, #EFF6FF 50%, #F8FAFC 100%)'
          }}
        />
      )}

      {/* Decorative blobs */}
      <div className="absolute top-0 right-0 w-64 h-64 bg-purple-500/5 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-40 left-0 w-48 h-48 bg-blue-500/5 rounded-full blur-3xl pointer-events-none" />

      {/* Tema geçiş butonu */}
      <motion.button
        whileTap={{ scale: 0.9 }}
        className={`fixed top-4 right-4 z-50 p-2.5 rounded-2xl shadow-lg transition-colors ${isDarkMode
          ? 'bg-slate-800 hover:bg-slate-700 text-amber-400'
          : 'bg-white hover:bg-slate-50 text-blue-600 border border-slate-200'
          }`}
        onClick={toggleTheme}
        aria-label={isDarkMode ? 'Aydınlık moda geç' : 'Karanlık moda geç'}
        type="button"
      >
        {isDarkMode ? <Sun size={20} /> : <Moon size={20} />}
      </motion.button>

      {!currentChat ? (
        /* Sohbet Listesi Ekranı */
        <>
          {/* Premium Header */}
          <header className={`sticky top-0 z-20 px-5 pt-12 pb-4 backdrop-blur-xl ${theme.headerBg}`}>
            <div className="flex justify-between items-center mb-4">
              <div>
                <h1 className={`text-2xl font-bold ${theme.text}`}>
                  {t('screens.messagingTitle', 'Mesajlar')}
                </h1>
                <p className={`text-xs ${theme.subText} mt-0.5`}>
                  <Lock size={10} className="inline mr-1" />
                  {t('security.endToEndEncrypted', 'Uçtan uca şifreli')}
                </p>
              </div>
            </div>

            {/* Modern Search Bar */}
            <motion.div
              animate={{
                scale: isSearchFocused ? 1.02 : 1,
                boxShadow: isSearchFocused
                  ? '0 0 0 2px rgba(139, 92, 246, 0.3)'
                  : '0 0 0 0px transparent'
              }}
              className={`relative rounded-2xl overflow-hidden ${theme.inputBg}`}
            >
              <input
                type="text"
                placeholder={t('messaging.searchChat', 'Sohbet veya kişi ara...')}
                className={`w-full p-3.5 pl-12 bg-transparent focus:outline-none ${theme.text}`}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onFocus={() => setIsSearchFocused(true)}
                onBlur={() => setIsSearchFocused(false)}
              />
              <Search size={18} className={`absolute left-4 top-1/2 transform -translate-y-1/2 ${theme.subText}`} />
            </motion.div>
          </header>

          {/* Tab Navigation */}
          <div className={`flex justify-center py-2 border-b ${theme.border}`}>
            {tabItems.map(tab => (
              <TabItem
                key={tab.id}
                active={activeTab === tab.id}
                label={tab.label}
                onClick={() => setActiveTab(tab.id)}
                isDarkMode={isDarkMode}
              />
            ))}
          </div>

          {/* Main Content */}
          <main className="flex-1 overflow-auto pb-navigation relative z-10">
            {activeTab === 'chats' && (
              <div className="py-4">
                {/* 🌟 Digital Clone - Always Pinned at Top */}
                <div className="px-2 mb-2">
                  <ChatCard
                    chat={DIGITAL_CLONE_CHAT}
                    onClick={() => handleSelectChat(DIGITAL_CLONE_CHAT)}
                    isDarkMode={isDarkMode}
                    isDigitalClone={true}
                  />
                </div>

                {/* Section Divider */}
                <div className="px-6 py-3 flex items-center gap-3">
                  <div className={`flex-1 h-px ${isDarkMode ? 'bg-slate-800' : 'bg-slate-200'}`} />
                  <span className={`text-xs font-medium ${theme.subText}`}>
                    <MessageCircle size={12} className="inline mr-1" />
                    Kişiler
                  </span>
                  <div className={`flex-1 h-px ${isDarkMode ? 'bg-slate-800' : 'bg-slate-200'}`} />
                </div>

                {/* Regular Chats */}
                <AnimatePresence>
                  {getFilteredConversations().map((chat, index) => (
                    <motion.div
                      key={chat.id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      transition={{ delay: index * 0.05 }}
                    >
                      <ChatCard
                        chat={chat}
                        onClick={() => handleSelectChat(chat)}
                        isDarkMode={isDarkMode}
                      />
                    </motion.div>
                  ))}
                </AnimatePresence>

                {getFilteredConversations().length === 0 && (
                  <div className="p-8 text-center">
                    <div className={`w-16 h-16 mx-auto mb-4 rounded-2xl ${theme.card} flex items-center justify-center`}>
                      <Search size={28} className={theme.subText} />
                    </div>
                    <p className={`font-medium ${theme.text}`}>
                      {t('messaging.noSearchResults', 'Sonuç bulunamadı')}
                    </p>
                    <p className={`text-sm ${theme.subText} mt-1`}>
                      "{searchQuery}" ile eşleşen sohbet yok
                    </p>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'groups' && (
              <div className="p-8 text-center">
                <div className={`w-20 h-20 mx-auto mb-4 rounded-3xl bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center`}>
                  <svg xmlns="http://www.w3.org/2000/svg" className="text-white" width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                    <circle cx="9" cy="7" r="4" />
                    <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                  </svg>
                </div>
                <h3 className={`text-lg font-bold ${theme.text} mb-2`}>
                  {t('messaging.noGroupChats', 'Henüz grup yok')}
                </h3>
                <p className={`text-sm ${theme.subText} mb-6`}>
                  Arkadaşlarınla güvenli gruplar oluştur
                </p>
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="px-6 py-3 bg-gradient-to-r from-blue-500 to-purple-500 text-white font-bold rounded-2xl shadow-lg shadow-blue-500/25"
                >
                  {t('messaging.createNewGroup', 'Yeni Grup Oluştur')}
                </motion.button>
              </div>
            )}

            {activeTab === 'secure' && (
              <div className="p-5 space-y-4">
                {/* Security Cards */}
                {[
                  {
                    icon: <Shield size={24} />,
                    color: 'from-emerald-500 to-teal-500',
                    title: t('security.endToEndEncryption', 'Uçtan Uca Şifreleme'),
                    desc: t('messaging.encryptionDescription', 'Tüm mesajlarınız şifrelenir ve sadece siz ve alıcı okuyabilir.')
                  },
                  {
                    icon: <Zap size={24} />,
                    color: 'from-amber-500 to-orange-500',
                    title: t('messaging.selfDestructMessages', 'Kendini İmha Eden Mesajlar'),
                    desc: t('messaging.selfDestructDescription', 'Okunduğunda otomatik silinen mesajlar gönderin.')
                  },
                  {
                    icon: <Brain size={24} />,
                    color: 'from-purple-500 to-pink-500',
                    title: t('messaging.aiAssistantPrivacy', 'AI Asistan Gizliliği'),
                    desc: t('messaging.aiPrivacyDescription', 'AI asistanınız cihazınızda çalışır, verileriniz sunuculara gönderilmez.'),
                    toggle: true
                  }
                ].map((item, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: i * 0.1 }}
                    className={`${theme.card} rounded-3xl p-5 shadow-sm ${isDarkMode ? 'border border-slate-800' : ''}`}
                  >
                    <div className="flex items-start gap-4">
                      <div className={`w-12 h-12 rounded-2xl bg-gradient-to-br ${item.color} flex items-center justify-center text-white flex-shrink-0`}>
                        {item.icon}
                      </div>
                      <div className="flex-1">
                        <h3 className={`font-bold ${theme.text} mb-1`}>{item.title}</h3>
                        <p className={`text-sm ${theme.subText}`}>{item.desc}</p>

                        {item.toggle && (
                          <>
                            <div className="flex items-center justify-between mt-4">
                              <span className={`text-sm font-medium ${theme.text}`}>
                                {t('messaging.aiAssistantActive', 'AI Asistanı Aktif')}
                              </span>
                              <button
                                onClick={() => setIsAIEnabled(!isAIEnabled)}
                                className={`relative w-12 h-7 rounded-full transition-colors ${isAIEnabled
                                  ? 'bg-gradient-to-r from-purple-500 to-pink-500'
                                  : isDarkMode ? 'bg-slate-700' : 'bg-slate-300'
                                  }`}
                              >
                                <motion.div
                                  animate={{ x: isAIEnabled ? 22 : 2 }}
                                  className="absolute top-1 w-5 h-5 bg-white rounded-full shadow"
                                />
                              </button>
                            </div>

                            {/* AI Öneri Yenileme Süresi Ayarı */}
                            {isAIEnabled && (
                              <div className={`flex items-center justify-between mt-4 pt-4 border-t ${isDarkMode ? 'border-slate-700/50' : 'border-slate-200'}`}>
                                <div>
                                  <span className={`text-sm font-medium ${theme.text}`}>
                                    AI Öneri Yenileme
                                  </span>
                                  <p className={`text-xs ${theme.subText} mt-0.5`}>
                                    {suggestionRefreshDelay} sn hareketsizlik sonrası
                                  </p>
                                </div>
                                <div className="flex items-center gap-2">
                                  <button
                                    onClick={() => setSuggestionRefreshDelay(prev => Math.max(5, prev - 5))}
                                    className={`w-8 h-8 rounded-full flex items-center justify-center text-lg font-bold ${isDarkMode ? 'bg-slate-700 text-white hover:bg-slate-600' : 'bg-slate-200 text-slate-700 hover:bg-slate-300'}`}
                                  >
                                    -
                                  </button>
                                  <span className={`font-bold w-8 text-center ${theme.text}`}>{suggestionRefreshDelay}</span>
                                  <button
                                    onClick={() => setSuggestionRefreshDelay(prev => Math.min(60, prev + 5))}
                                    className={`w-8 h-8 rounded-full flex items-center justify-center text-lg font-bold ${isDarkMode ? 'bg-slate-700 text-white hover:bg-slate-600' : 'bg-slate-200 text-slate-700 hover:bg-slate-300'}`}
                                  >
                                    +
                                  </button>
                                </div>
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </main>
        </>
      ) : (
        /* Chat Conversation View */
        <>
          {/* Chat Header */}
          <header className={`sticky top-0 z-20 px-4 py-3 backdrop-blur-xl ${theme.headerBg} border-b ${theme.border}`}>
            <div className="flex items-center gap-3">
              <motion.button
                whileTap={{ scale: 0.9 }}
                className={`p-2 rounded-xl ${isDarkMode ? 'hover:bg-slate-800' : 'hover:bg-slate-100'}`}
                onClick={() => setCurrentChat(null)}
              >
                <ArrowLeft size={22} className={theme.text} />
              </motion.button>

              {/* Clickable Avatar - Opens Profile */}
              <motion.div
                className="relative cursor-pointer"
                whileTap={{ scale: 0.95 }}
                onClick={() => !currentChat.isAI && setShowProfileModal(currentChat)}
              >
                <div
                  className={`w-12 h-12 rounded-2xl flex items-center justify-center font-bold ${currentChat.isAI
                    ? 'bg-gradient-to-br from-purple-500 to-pink-500'
                    : 'bg-gradient-to-br from-blue-500 to-cyan-400'
                    }`}
                >
                  {currentChat.isAI ? (
                    <Brain size={24} className="text-white" />
                  ) : (
                    <span className="text-white">{currentChat.name.charAt(0)}</span>
                  )}
                </div>
                {currentChat.online && (
                  <div className="absolute -bottom-0.5 -right-0.5 w-4 h-4 bg-emerald-500 rounded-full border-2 border-white dark:border-slate-900" />
                )}
              </motion.div>

              {/* Clickable Name - Opens Profile */}
              <div
                className="flex-1 min-w-0 cursor-pointer"
                onClick={() => !currentChat.isAI && setShowProfileModal(currentChat)}
              >
                <div className="flex items-center gap-2">
                  <h2 className={`font-bold ${theme.text}`}>{currentChat.name}</h2>
                  {currentChat.isAI && (
                    <span className="text-[10px] font-bold bg-purple-500/20 text-purple-400 px-2 py-0.5 rounded-full">
                      AI
                    </span>
                  )}
                  {currentChat.verified && !currentChat.isAI && (
                    <div className="w-4 h-4 bg-blue-500 rounded-full flex items-center justify-center">
                      <Check size={10} className="text-white" strokeWidth={3} />
                    </div>
                  )}
                </div>
                <p className={`text-xs ${theme.subText}`}>
                  {currentChat.online
                    ? currentChat.isAI
                      ? 'Aktif • Seni dinliyor'
                      : t('messaging.online', 'Çevrimiçi')
                    : t('messaging.lastSeen', 'Son görülme: Bugün 15:42')
                  }
                </p>
              </div>

              {/* Action Icons - Video, Phone, More */}
              <div className="flex gap-1">
                {!currentChat.isAI && (
                  <>
                    {/* Icons removed as per user request */}
                  </>
                )}
                <motion.button
                  whileTap={{ scale: 0.9 }}
                  className={`p-2 rounded-xl ${isDarkMode ? 'hover:bg-slate-800' : 'hover:bg-slate-100'}`}
                >
                  <MoreVertical size={20} className={isDarkMode ? 'text-slate-400' : 'text-slate-600'} />
                </motion.button>
              </div>
            </div>
          </header>

          {/* Messages Area - Fixed height to ensure input is visible */}
          <main className={`flex-1 overflow-auto p-4 ${theme.background}`} style={{ minHeight: 0 }}>
            {/* Security Notice */}
            <div className="flex justify-center mb-6">
              <div className={`flex items-center gap-2 px-4 py-2 rounded-full ${isDarkMode ? 'bg-slate-800/50' : 'bg-white shadow-sm'
                }`}>
                <Lock size={12} className={getPrivacyColor()} />
                <span className={`text-xs ${theme.subText}`}>{getPrivacyInfo()}</span>
              </div>
            </div>

            {/* Messages */}
            <div className="space-y-1 pb-4">
              {currentChat.messages.map((msg) => (
                <MessageBubble
                  key={msg.id}
                  message={msg}
                  isDarkMode={isDarkMode}
                />
              ))}
              <div ref={messagesEndRef} />
            </div>
          </main>

          {/* AI Suggestions - Swipeable & Touch Optimized */}
          <AnimatePresence>
            {isAIEnabled && aiSuggestions.length > 0 && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 20 }}
                className={`px-4 py-3 border-t relative z-20 w-full overflow-x-auto no-scrollbar ${isDarkMode ? 'border-slate-800 bg-slate-900' : 'border-slate-200 bg-gray-50'}`}
                style={{
                  WebkitOverflowScrolling: 'touch',
                  display: 'flex',
                  gap: '0.5rem'
                }}
              >
                <div className="flex items-center gap-2 mb-2 flex-shrink-0 mr-2">
                  <Sparkles size={14} className={isDarkMode ? 'text-purple-400' : 'text-purple-500'} />
                </div>
                {aiSuggestions.map((suggestion, index) => (
                  <AISuggestionChip
                    key={index}
                    text={suggestion}
                    onClick={() => applySuggestion(suggestion)}
                    isDarkMode={isDarkMode}
                  />
                ))}
              </motion.div>
            )}
          </AnimatePresence>

          {/* Message Input - Consolidated Buttons */}
          <div className={`p-3 border-t ${theme.border} ${isDarkMode ? 'bg-slate-900' : 'bg-white'} flex-shrink-0 relative z-30 w-full pb-[calc(1rem+env(safe-area-inset-bottom))]`}>

            {/* Attachment Menu Popup */}
            <AnimatePresence>
              {showAttachMenu && (
                <motion.div
                  initial={{ opacity: 0, y: 20, scale: 0.9 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: 20, scale: 0.9 }}
                  className={`absolute bottom-full left-4 mb-4 p-2 rounded-2xl shadow-xl border flex flex-col gap-2 ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'
                    }`}
                  style={{ minWidth: '160px' }}
                >
                  <button
                    onClick={() => handleAttachment('gallery')}
                    className={`flex items-center gap-3 p-3 rounded-xl transition-colors ${isDarkMode ? 'hover:bg-slate-700' : 'hover:bg-slate-100'}`}
                  >
                    <div className="w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center text-purple-600">
                      <Image size={18} />
                    </div>
                    <span className={`text-sm font-medium ${theme.text}`}>Galeri</span>
                  </button>
                  <button
                    onClick={() => handleAttachment('document')}
                    className={`flex items-center gap-3 p-3 rounded-xl transition-colors ${isDarkMode ? 'hover:bg-slate-700' : 'hover:bg-slate-100'}`}
                  >
                    <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-600">
                      <FileText size={18} />
                    </div>
                    <span className={`text-sm font-medium ${theme.text}`}>Belge</span>
                  </button>
                  <button
                    onClick={() => handleAttachment('location')}
                    className={`flex items-center gap-3 p-3 rounded-xl transition-colors ${isDarkMode ? 'hover:bg-slate-700' : 'hover:bg-slate-100'}`}
                  >
                    <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center text-green-600">
                      <MapPin size={18} />
                    </div>
                    <span className={`text-sm font-medium ${theme.text}`}>Konum</span>
                  </button>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Hidden Inputs for Attachments */}
            <input
              type="file"
              ref={imageInputRef}
              className="hidden"
              accept="image/*"
              onChange={(e) => handleFileChange(e, 'image')}
            />
            <input
              type="file"
              ref={fileInputRef}
              className="hidden"
              accept="*"
              onChange={(e) => handleFileChange(e, 'document')}
            />

            <div className="flex items-end gap-2">

              {/* Voice Recording Preview Mode */}
              {voiceState.audioUrl ? (
                <div className={`flex-1 flex items-center gap-3 px-4 py-2 rounded-2xl ${isDarkMode ? 'bg-slate-800' : 'bg-slate-100'}`}>
                  <motion.button
                    whileTap={{ scale: 0.9 }}
                    onClick={cancelRecording}
                    className="p-2 rounded-full bg-red-100 text-red-500"
                  >
                    <Trash2 size={20} />
                  </motion.button>

                  <div className="flex-1 flex items-center gap-3">
                    <motion.button
                      whileTap={{ scale: 0.9 }}
                      onClick={togglePreviewPlay}
                      className={`p-2 rounded-full ${isDarkMode ? 'bg-slate-700 text-white' : 'bg-white text-slate-900'} shadow-sm`}
                    >
                      {voiceState.isPlaying ? <Pause size={18} /> : <Play size={18} />}
                    </motion.button>
                    <div className="h-1 flex-1 bg-gray-300 rounded-full overflow-hidden">
                      <motion.div
                        className="h-full bg-blue-500"
                        animate={{ width: voiceState.isPlaying ? '100%' : '0%' }}
                        transition={{ duration: voiceState.recordingDuration, ease: "linear" }}
                      />
                    </div>
                    <span className={`text-xs font-mono font-medium ${theme.text}`}>
                      {formatTime(voiceState.recordingDuration)}
                    </span>
                  </div>

                  <motion.button
                    whileTap={{ scale: 0.9 }}
                    onClick={handleSendMessage}
                    className="p-2 rounded-full bg-green-500 text-white shadow-lg shadow-green-500/30"
                  >
                    <Send size={20} className="ml-0.5" />
                  </motion.button>
                </div>
              ) : (
                <>
                  {/* Left: Attachment Toggle */}
                  <motion.button
                    whileTap={{ scale: 0.9 }}
                    onClick={() => setShowAttachMenu(!showAttachMenu)}
                    className={`p-3 rounded-full flex-shrink-0 transition-colors ${showAttachMenu
                      ? 'bg-gradient-to-r from-blue-500 to-purple-500 text-white rotate-45'
                      : isDarkMode ? 'bg-slate-800 text-slate-400 hover:bg-slate-700' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'
                      }`}
                    style={{ transition: 'all 0.3s ease' }}
                  >
                    <Plus size={24} style={{ transform: showAttachMenu ? 'rotate(45deg)' : 'none', transition: 'transform 0.3s' }} />
                  </motion.button>

                  {/* Center: Text Input */}
                  <div className={`flex-1 rounded-2xl px-4 py-3 min-h-[50px] flex items-center ${theme.inputBg}`}>
                    {voiceState.isRecording ? (
                      <div className="flex items-center gap-3 w-full animate-pulse">
                        <div className="w-3 h-3 rounded-full bg-red-500"></div>
                        <span className="text-red-500 font-medium font-mono">{formatTime(voiceState.recordingDuration)}</span>
                        <div className="flex-1 text-right text-xs text-slate-400">Parmağını çekerek kaydı bitir</div>
                      </div>
                    ) : (
                      <input
                        type="text"
                        placeholder={t('messaging.writeMessage', 'Mesaj yazın...')}
                        className={`w-full bg-transparent border-0 focus:outline-none ${theme.text}`}
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                        onKeyPress={(e) => {
                          if (e.key === 'Enter') {
                            handleSendMessage();
                          }
                        }}
                      />
                    )}
                  </div>

                  {/* Right: Mic / Send Button */}
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onMouseDown={() => {
                      if (!message.trim()) {
                        recordingTimeoutRef.current = setTimeout(startRecording, 500);
                      }
                    }}
                    onMouseUp={() => {
                      if (recordingTimeoutRef.current) clearTimeout(recordingTimeoutRef.current);
                      if (voiceState.isRecording) stopRecording();
                    }}
                    onTouchStart={() => {
                      if (!message.trim()) {
                        recordingTimeoutRef.current = setTimeout(startRecording, 500);
                      }
                    }}
                    onTouchEnd={() => {
                      if (recordingTimeoutRef.current) clearTimeout(recordingTimeoutRef.current);
                      if (voiceState.isRecording) stopRecording();
                    }}
                    onClick={() => {
                      if (message.trim()) handleSendMessage();
                    }}
                    className={`p-3 rounded-full flex-shrink-0 shadow-lg flex items-center justify-center transition-all ${message.trim() || voiceState.isRecording
                      ? 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-blue-500/30'
                      : isDarkMode ? 'bg-slate-800 text-slate-400' : 'bg-slate-200 text-slate-500'
                      } ${voiceState.isRecording ? 'scale-125 ring-4 ring-red-500/20 bg-red-500' : ''}`}
                    style={{ width: '50px', height: '50px' }}
                  >
                    <AnimatePresence mode='wait'>
                      {message.trim() ? (
                        <motion.div
                          key="send"
                          initial={{ scale: 0 }}
                          animate={{ scale: 1 }}
                          exit={{ scale: 0 }}
                        >
                          <Send size={20} className="ml-0.5" />
                        </motion.div>
                      ) : (
                        <motion.div
                          key="mic"
                          initial={{ scale: 0 }}
                          animate={{ scale: 1 }}
                          exit={{ scale: 0 }}
                        >
                          <Mic size={24} className={voiceState.isRecording ? 'text-white' : ''} />
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </motion.button>
                </>
              )}
            </div>
          </div>
        </>
      )}

      {/* Profile Modal */}
      <AnimatePresence>
        {showProfileModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            style={{
              background: 'rgba(0, 0, 0, 0.6)',
              backdropFilter: 'blur(20px)',
              WebkitBackdropFilter: 'blur(20px)'
            }}
            onClick={() => setShowProfileModal(null)}
          >
            <motion.div
              initial={{ scale: 0.8, opacity: 0, y: 50 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.8, opacity: 0, y: 50 }}
              transition={{ type: "spring", damping: 25, stiffness: 300 }}
              drag="y"
              dragConstraints={{ top: 0, bottom: 200 }}
              onDragEnd={(event, info) => {
                if (info.offset.y > 100 && info.velocity.y > 0) {
                  setShowProfileModal(null);
                }
              }}
              className="relative w-full max-w-md mx-auto rounded-3xl overflow-hidden"
              style={{
                background: isDarkMode ? 'rgba(15, 23, 42, 0.95)' : 'rgba(255, 255, 255, 0.95)',
                backdropFilter: 'blur(40px)',
                border: isDarkMode ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid rgba(0, 0, 0, 0.1)',
                boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)'
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Drag Indicator */}
              <div className="flex justify-center pt-3 pb-2">
                <div className={`w-12 h-1 rounded-full ${isDarkMode ? 'bg-white/20' : 'bg-black/20'}`}></div>
              </div>

              {/* Close Button */}
              <button
                onClick={() => setShowProfileModal(null)}
                className={`absolute top-4 right-4 z-10 p-2 rounded-full transition-colors ${isDarkMode ? 'bg-white/10 hover:bg-white/20' : 'bg-black/10 hover:bg-black/20'}`}
              >
                <X size={20} className={isDarkMode ? 'text-white' : 'text-black'} />
              </button>

              {/* Profile Header */}
              <div className="relative px-6 pt-4 pb-8">
                {/* Avatar with Glow */}
                <div className="flex justify-center mb-4">
                  <div className="relative">
                    {/* Glow Effect */}
                    <div
                      className="absolute inset-0 rounded-full blur-xl"
                      style={{
                        background: 'radial-gradient(circle, rgba(59, 130, 246, 0.5) 0%, rgba(139, 92, 246, 0.3) 50%, transparent 70%)',
                        transform: 'scale(1.5)'
                      }}
                    ></div>

                    {/* Avatar Circle */}
                    <div className="relative w-24 h-24 rounded-full bg-gradient-to-br from-blue-500 to-cyan-400 flex items-center justify-center border-4 border-white/20 shadow-xl">
                      <span className="text-4xl font-bold text-white">
                        {showProfileModal.name?.charAt(0)?.toUpperCase() || 'U'}
                      </span>
                    </div>

                    {/* Online Status */}
                    {showProfileModal.online && (
                      <div className="absolute -bottom-1 -right-1 w-7 h-7 bg-emerald-500 rounded-full border-4 border-white dark:border-slate-800 flex items-center justify-center">
                        <div className="w-2 h-2 bg-white rounded-full"></div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Name and Status */}
                <div className="text-center mb-6">
                  <h2 className={`text-2xl font-bold mb-1 ${theme.text}`}>{showProfileModal.name}</h2>
                  <p className={`text-sm ${theme.subText}`}>
                    {showProfileModal.online ? 'Çevrimiçi' : 'Son görülme: Bugün 15:42'}
                  </p>

                  {/* Verified Badge */}
                  {showProfileModal.verified && (
                    <div className="inline-flex items-center mt-2 px-3 py-1 rounded-full bg-blue-500/10 border border-blue-500/20">
                      <Check size={14} className="text-blue-500 mr-1" />
                      <span className="text-sm text-blue-500 font-medium">Doğrulanmış</span>
                    </div>
                  )}
                </div>
              </div>

              {/* XP Progress - Matches Screenshot */}
              <div className="px-6 mb-6">
                <div className="flex justify-between items-center mb-2">
                  <span className={`text-sm ${theme.subText}`}>XP İlerlemesi</span>
                  <span className={`text-sm ${isDarkMode ? 'text-purple-300' : 'text-purple-600'}`}>1250 / 1400</span>
                </div>
                <div className={`w-full h-2 rounded-full ${isDarkMode ? 'bg-slate-800' : 'bg-slate-200'}`}>
                  <div
                    className="h-full rounded-full bg-gradient-to-r from-purple-500 to-pink-500"
                    style={{ width: '87%' }}
                  />
                </div>
              </div>

              {/* Action Buttons - Updated per request (No Call/Video) */}
              <div className="px-6 mb-8">
                <div className="grid grid-cols-3 gap-3">
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => setShowProfileModal(null)}
                    className={`flex flex-col items-center justify-center p-4 rounded-2xl transition-all h-24 ${isDarkMode
                      ? 'bg-blue-500/20 border border-blue-500/30'
                      : 'bg-blue-50 border border-blue-200'
                      }`}
                  >
                    <MessageCircle size={24} className="text-blue-500 mb-2" />
                    <span className={`text-xs font-medium ${isDarkMode ? 'text-blue-300' : 'text-blue-600'}`}>Mesaj</span>
                  </motion.button>

                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => {
                      setShowProfileModal(null);
                      navigateToScreen('profile', { viewUser: showProfileModal });
                    }}
                    className={`flex flex-col items-center justify-center p-4 rounded-2xl transition-all h-24 ${isDarkMode
                      ? 'bg-purple-500/20 border border-purple-500/30'
                      : 'bg-purple-50 border border-purple-200'
                      }`}
                  >
                    <User size={24} className="text-purple-500 mb-2" />
                    <span className={`text-xs font-medium ${isDarkMode ? 'text-purple-300' : 'text-purple-600'}`}>Profil</span>
                  </motion.button>

                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className={`flex flex-col items-center justify-center p-4 rounded-2xl transition-all h-24 ${isDarkMode
                      ? 'bg-red-500/20 border border-red-500/30'
                      : 'bg-red-50 border border-red-200'
                      }`}
                  >
                    <VolumeX size={24} className="text-red-500 mb-2" />
                    <span className={`text-xs font-medium ${isDarkMode ? 'text-red-300' : 'text-red-600'}`}>Sessize Al</span>
                  </motion.button>
                </div>
              </div>

              {/* Recent Activities Section */}
              <div className="px-6 mb-6">
                <div className="flex items-center gap-2 mb-4">
                  <Zap size={18} className="text-amber-400" />
                  <h3 className={`font-bold ${theme.text}`}>Son Aktiviteler</h3>
                </div>

                <div className="space-y-3">
                  <div className={`p-4 rounded-2xl ${isDarkMode ? 'bg-slate-800/50' : 'bg-slate-50 border border-slate-100'}`}>
                    <div className="flex justify-between items-start mb-1">
                      <p className={`text-sm font-medium ${theme.text}`}>Az önce harika bir antrenman yaptım! 💪</p>
                    </div>
                    <div className="flex justify-between items-center text-xs mt-2">
                      <span className={theme.subText}>2 saat önce</span>
                      <span className="flex items-center gap-1 text-pink-500">
                        <Heart size={12} fill="currentColor" /> 15
                      </span>
                    </div>
                  </div>

                  <div className={`p-4 rounded-2xl ${isDarkMode ? 'bg-slate-800/50' : 'bg-slate-50 border border-slate-100'}`}>
                    <div className="flex justify-between items-start mb-1">
                      <p className={`text-sm font-medium ${theme.text}`}>Bugün heyecan verici projeler üzerinde çalışıyorum 🚀</p>
                    </div>
                    <div className="flex justify-between items-center text-xs mt-2">
                      <span className={theme.subText}>1 gün önce</span>
                      <span className="flex items-center gap-1 text-pink-500">
                        <Heart size={12} /> 8
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Mutual Connections */}
              <div className="px-6 pb-6">
                <div className="flex items-center gap-2 mb-4">
                  <Users size={18} className="text-emerald-400" />
                  <h3 className={`font-bold ${theme.text}`}>Ortak Bağlantılar</h3>
                </div>

                <div className="flex items-center gap-4">
                  <div className="flex -space-x-3">
                    <div className="w-10 h-10 rounded-full bg-blue-500 border-2 border-slate-900 flex items-center justify-center text-white text-xs font-bold">S</div>
                    <div className="w-10 h-10 rounded-full bg-purple-500 border-2 border-slate-900 flex items-center justify-center text-white text-xs font-bold">A</div>
                    <div className="w-10 h-10 rounded-full bg-amber-500 border-2 border-slate-900 flex items-center justify-center text-white text-xs font-bold">D</div>
                  </div>
                  <span className={`text-sm ${theme.subText}`}>+2 daha</span>
                </div>
                <div className="flex gap-4 mt-2 pl-1">
                  <span className={`text-xs ${theme.subText}`}>Sarah ...</span>
                  <span className={`text-xs ${theme.subText}`}>Alex Kim</span>
                  <span className={`text-xs ${theme.subText}`}>Dev Te...</span>
                </div>
              </div>

              {/* Bottom Gesture Hint */}
              <div className="flex justify-center pb-4">
                <div className={`flex items-center text-xs ${theme.subText}`}>
                  <ChevronDown size={14} className="mr-1" />
                  Kapatmak için aşağı kaydır
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Bottom Navigation - Only on chat list */}
      {!currentChat && <BottomNavigation activeItem="messages" />}
    </div>
  );
};

export default MessagingScreen;